import { environment } from "@app/relay/relay-environment";
import AwsS3 from "@uppy/aws-s3";
import Uppy from "@uppy/core";
import type { UppyFile } from "@uppy/core";
import { DragDrop } from "@uppy/react";
import { useRouter } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { commitMutation, fetchQuery, graphql } from "react-relay";

import type { accountDropzone_PresignQuery } from "@app/queries/accountDropzone_PresignQuery.graphql";
import type { accountDropzone_AccountIngestStartMutation } from "@app/queries/accountDropzone_AccountIngestStartMutation.graphql";
import type { accountDropzone_BankAccountCreateMutation } from "@app/queries/accountDropzone_BankAccountCreateMutation.graphql";
import type { accountDropzone_PollQuery } from "@app/queries/accountDropzone_PollQuery.graphql";

import "@uppy/core/dist/style.min.css";
import "@uppy/drag-drop/dist/style.min.css";

const presignQuery = graphql`
  query accountDropzone_PresignQuery($fileExtension: String!) {
    uploadPresign(fileExtension: $fileExtension) {
      signedUrl
      storageKey
    }
  }
`;

const ingestStartMutation = graphql`
  mutation accountDropzone_AccountIngestStartMutation(
    $multiUploadInput: MultiUploadInputObject!
    $targetAccountNumber: String
    $confirmUnverified: Boolean
  ) {
    accountIngestStart(
      multiUploadInput: $multiUploadInput
      targetAccountNumber: $targetAccountNumber
      confirmUnverified: $confirmUnverified
    ) {
      multiUpload {
        id
        phase
      }
      errors {
        message
      }
      warnings
    }
  }
`;

const bankAccountCreateMutation = graphql`
  mutation accountDropzone_BankAccountCreateMutation(
    $number: String!
    $nickname: String
    $kind: String!
  ) {
    bankAccountCreate(number: $number, nickname: $nickname, kind: $kind) {
      number
      errors {
        message
      }
    }
  }
`;

const pollQuery = graphql`
  query accountDropzone_PollQuery($id: ID!) {
    multiUpload(id: $id) {
      phase
      state
      errorMessage
      notice
    }
  }
`;

export type DropzoneAccount = {
  number: string;
  nickname: string | null;
  kind: string;
};

// Staged progress: each backend phase maps to a label + bar fill. "UPLOADING" is
// the client-side S3 transfer before the worker starts.
const PHASES: Record<string, { label: string; pct: number }> = {
  UPLOADING: { label: "Uploading", pct: 12 },
  QUEUED: { label: "Queued", pct: 22 },
  PARSING: { label: "Parsing", pct: 42 },
  ATTRIBUTING: { label: "Applying rules", pct: 66 },
  CATEGORIZING: { label: "Categorizing", pct: 86 },
  DONE: { label: "Done", pct: 100 },
};

type Status = "idle" | "mismatch" | "running" | "unverified" | "error";

type UploadedDescriptor = {
  id: string;
  storage: "cache";
  metadata: { size: number; filename: string; mimeType: string };
};

// The card a dropped file is for, guessed from its filename: the last-4 for a
// numbered card ("Chase1234" or a "(...1234)" suffix), or "wise"/"deel". Returns
// "" when it can't tell. Used to catch a file dropped on the wrong card.
function guessNumber(filename: string): string {
  const chase = filename.match(/chase(\d{4})/i);
  if (chase) return chase[1];
  const paren = filename.match(/\(\.\.\.(\d{4})\)/); // e.g. "(...3512)"
  if (paren) return paren[1];
  const lower = filename.toLowerCase();
  if (lower.includes("wise") || lower.includes("transaction-history")) return "wise";
  if (lower.includes("deel")) return "deel";
  return "";
}

export default function AccountDropzone({
  account,
  accounts,
}: {
  account: DropzoneAccount;
  accounts: DropzoneAccount[];
}) {
  const router = useRouter();
  const [status, setStatus] = useState<Status>("idle");
  const [phase, setPhase] = useState<string>("UPLOADING");
  const [error, setError] = useState<string | null>(null);
  // Non-blocking preflight warnings from the ingest mutation (e.g. possible
  // double-count on a QBO-sourced account) — the import still proceeds.
  const [warnings, setWarnings] = useState<readonly string[]>([]);

  // The dropped file's name, and the card its filename points at when that differs
  // from this one (awaiting the user's choice).
  const [pendingFilename, setPendingFilename] = useState<string>("");
  const [detectedNumber, setDetectedNumber] = useState<string>("");

  const descriptorRef = useRef<UploadedDescriptor | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Account the current file was routed to, kept so "Import anyway" can re-ingest
  // the same upload (descriptorRef) after a failed ownership check.
  const ingestTargetRef = useRef<string | null>(null);

  // Created inside an effect (below) rather than useMemo. Under React StrictMode the
  // mount→unmount→remount cycle runs the cleanup's uppy.close() — which uninstalls the
  // AwsS3 uploader — but a useMemo factory never re-runs to re-add it, so uploads end up
  // with no uploader and silently "succeed" without ever reaching S3. Owning creation in
  // the effect keeps the instance and its uploader in lockstep across that cycle.
  const [uppy, setUppy] = useState<Uppy | null>(null);

  const reset = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
    descriptorRef.current = null;
    setPendingFilename("");
    setDetectedNumber("");
    uppy?.cancelAll();
  };

  const startPolling = (multiUploadId: string) => {
    pollRef.current = setInterval(() => {
      fetchQuery<accountDropzone_PollQuery>(environment, pollQuery, {
        id: multiUploadId,
      })
        .toPromise()
        .then((data) => {
          const mu = data?.multiUpload;
          if (!mu) return;
          if (mu.phase) setPhase(mu.phase);
          if (mu.phase === "DONE") {
            // Rows the parse dropped or re-dated to make the file importable. Not a
            // failure, so it lands in the amber warnings channel rather than the
            // error one — but it changed what got counted, so it is not silent.
            const notice = mu.notice;
            if (notice) setWarnings((prev) => [...prev, notice]);
            reset();
            setStatus("idle");
            router.invalidate();
          } else if (mu.phase === "UNVERIFIED") {
            // Couldn't confirm the file belongs to the account. Stop polling but
            // keep the descriptor + target so "Import anyway" can re-ingest.
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = null;
            setStatus("unverified");
          } else if (mu.phase === "FAILED" || mu.state === "FAILED") {
            setError(mu.errorMessage ?? "Import failed");
            setStatus("error");
            reset();
          }
        });
    }, 1000);
  };

  const startIngest = (
    targetAccountNumber: string,
    confirmUnverified = false
  ) => {
    const descriptor = descriptorRef.current;
    if (!descriptor) return;
    ingestTargetRef.current = targetAccountNumber;
    setStatus("running");
    setPhase("QUEUED");
    commitMutation<accountDropzone_AccountIngestStartMutation>(environment, {
      mutation: ingestStartMutation,
      variables: {
        multiUploadInput: { uploads: [descriptor] },
        targetAccountNumber,
        confirmUnverified,
      },
      onCompleted: (response) => {
        const result = response.accountIngestStart;
        setWarnings(result?.warnings ?? []);
        const id = result?.multiUpload?.id;
        if (id) {
          startPolling(id);
        } else {
          setError(result?.errors?.[0]?.message ?? "Import failed");
          setStatus("error");
          reset();
        }
      },
      onError: (err) => {
        setError(err.message);
        setStatus("error");
        reset();
      },
    });
  };

  // Once the file is on S3, route it: matching filename -> straight to this card;
  // mismatch -> wait for the user to pick or create a target.
  const onUploadSuccess = (file: UppyFile | undefined) => {
    if (!file) return;
    descriptorRef.current = {
      id: String(file.meta.shrineId),
      storage: "cache",
      metadata: {
        size: file.size,
        filename: file.name,
        mimeType: file.type ?? "text/csv",
      },
    };
    // If the filename points at a different card, ask before importing here.
    const detected = guessNumber(file.name);
    if (detected && detected !== account.number) {
      setDetectedNumber(detected);
      setStatus("mismatch");
    } else {
      startIngest(account.number);
    }
  };

  const onFileAdded = (file: UppyFile) => {
    setError(null);
    setWarnings([]);
    setPhase("UPLOADING");
    setStatus("running");
    setPendingFilename(file.name);
    uppy?.upload();
  };

  // Own the Uppy instance's full lifecycle here so it survives StrictMode's
  // mount→unmount→remount: a remount rebuilds the instance with its AwsS3 uploader,
  // and a real unmount closes it (removing Uppy's window listeners).
  useEffect(() => {
    const instance = new Uppy({
      restrictions: { allowedFileTypes: [".csv"], maxNumberOfFiles: 1 },
      autoProceed: false,
    }).use(AwsS3, {
      async getUploadParameters(file: UppyFile) {
        const extension = file.name.split(".").pop() ?? "csv";
        const data = await fetchQuery<accountDropzone_PresignQuery>(
          environment,
          presignQuery,
          {
            fileExtension: extension,
          }
        ).toPromise();
        const presign = data?.uploadPresign;
        if (!presign) throw new Error("Could not presign upload");
        instance.setFileMeta(file.id, { shrineId: presign.storageKey });
        return {
          method: "PUT",
          url: presign.signedUrl,
          fields: {},
          headers: { "Content-Type": file.type ?? "text/csv" },
        };
      },
    });
    setUppy(instance);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      instance.close();
    };
  }, []);

  useEffect(() => {
    if (!uppy) return;
    uppy.on("file-added", onFileAdded);
    uppy.on("upload-success", onUploadSuccess);
    return () => {
      uppy.off("file-added", onFileAdded);
      uppy.off("upload-success", onUploadSuccess);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uppy]);

  // Create a new CARD account for the detected last-4, then import into it.
  const createCardAndIngest = (number: string) => {
    setStatus("running");
    commitMutation<accountDropzone_BankAccountCreateMutation>(environment, {
      mutation: bankAccountCreateMutation,
      variables: { number, nickname: `Card ${number}`, kind: "CARD" },
      onCompleted: (response) => {
        const result = response.bankAccountCreate;
        if (result?.number) {
          startIngest(result.number);
        } else {
          setError(
            result?.errors?.[0]?.message ?? "Could not create card account"
          );
          setStatus("error");
          reset();
        }
      },
      onError: (err) => {
        setError(err.message);
        setStatus("error");
        reset();
      },
    });
  };

  // --- Rendering ------------------------------------------------------------

  if (status === "running") {
    const info = PHASES[phase] ?? PHASES.UPLOADING;
    return (
      <div className="px-4 py-2">
        <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
          <span>{info.label}…</span>
          <span>{info.pct}%</span>
        </div>
        <div className="h-1.5 w-full rounded bg-gray-100 overflow-hidden">
          <div
            className="h-full bg-gray-900 transition-all duration-500 ease-out"
            style={{ width: `${info.pct}%` }}
          />
        </div>
        {warnings.map((warning) => (
          <p
            key={warning}
            className="text-xs text-amber-700 mt-1 whitespace-pre-line"
          >
            {warning}
          </p>
        ))}
      </div>
    );
  }

  if (status === "unverified") {
    return (
      <div className="px-4 py-2.5 bg-amber-50 border-t border-amber-200 space-y-2 text-sm">
        <p className="text-amber-800 text-xs">
          Couldn't verify <span className="font-mono">{pendingFilename}</span>{" "}
          belongs to{" "}
          <span className="font-medium">
            {account.nickname ?? account.number}
          </span>{" "}
          — neither its name nor its contents mention{" "}
          <span className="font-mono">{account.number}</span>. Import it anyway,
          or rename the file to include {account.number} and re-drop.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() =>
              startIngest(ingestTargetRef.current ?? account.number, true)
            }
            className="rounded bg-gray-900 text-white px-2.5 py-1 text-xs font-medium"
          >
            Import anyway
          </button>
          <button
            type="button"
            onClick={() => {
              reset();
              setStatus("idle");
            }}
            className="text-xs text-gray-400 hover:text-gray-600"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (status === "mismatch") {
    const detectedExists = accounts.some((a) => a.number === detectedNumber);
    return (
      <div className="px-4 py-2.5 bg-amber-50 border-t border-amber-200 space-y-2 text-sm">
        <p className="text-amber-800 text-xs">
          <span className="font-mono">{pendingFilename}</span> looks like a
          transactions file for card{" "}
          <span className="font-mono">{detectedNumber}</span>, not{" "}
          <span className="font-medium">
            {account.nickname ?? account.number}
          </span>
          .{" "}
          {detectedExists
            ? `Send it to the existing ${detectedNumber} card?`
            : `Do you want to create a new card account for ${detectedNumber}?`}
        </p>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() =>
              detectedExists
                ? startIngest(detectedNumber)
                : createCardAndIngest(detectedNumber)
            }
            className="rounded bg-gray-900 text-white px-2.5 py-1 text-xs font-medium"
          >
            {detectedExists
              ? `Yes, send to ${detectedNumber}`
              : `Yes, create card ${detectedNumber}`}
          </button>
          <button
            type="button"
            onClick={() => startIngest(account.number, true)}
            className="rounded border px-2 py-1 text-xs font-medium text-gray-700"
          >
            No, the file is misnamed — import to{" "}
            {account.nickname ?? account.number}
          </button>
          <button
            type="button"
            onClick={() => {
              reset();
              setStatus("idle");
            }}
            className="text-xs text-gray-400 hover:text-gray-600"
          >
            No, cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-1.5">
      <div className="[&_.uppy-DragDrop-container]:min-h-0 [&_.uppy-DragDrop-inner]:py-2 [&_.uppy-DragDrop-label]:text-xs">
        {uppy && (
          <DragDrop
            uppy={uppy}
            height="40px"
            note={`Drop a CSV for ${account.nickname ?? account.number}`}
          />
        )}
      </div>
      {status === "error" && error && (
        <p className="text-xs text-red-600 mt-1">{error}</p>
      )}
      {warnings.map((warning) => (
        <p
          key={warning}
          className="text-xs text-amber-700 mt-1 whitespace-pre-line"
        >
          {warning}
        </p>
      ))}
    </div>
  );
}
